import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FiUser, FiMail, FiLock, FiGlobe, FiShield } from 'react-icons/fi';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { FormField } from '@/shared/components/ui/FormField';
import { AdminAuthService } from '@/modules/auth/services/auth.service';
import { toast } from 'sonner';

const registerSchema = z.object({
  appName: z.string().min(2, 'Mínimo 2 caracteres'),
  domain: z.string().min(3, 'Domínio inválido'),
  adminDomain: z.string().min(3, 'Domínio admin inválido'),
  firstName: z.string().min(2, 'Mínimo 2 caracteres'),
  lastName: z.string().min(2, 'Mínimo 2 caracteres'),
  email: z.string().email('Email inválido'),
  password: z.string().min(6, 'Mínimo 6 caracteres'),
  confirmPassword: z.string().min(6, 'Mínimo 6 caracteres'),
}).refine(data => data.password === data.confirmPassword, {
  message: 'Senhas não coincidem',
  path: ['confirmPassword'],
});

type RegisterFormData = z.infer<typeof registerSchema>;

export const Register: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setIsLoading(true);
      await AdminAuthService.register({
        appName: data.appName,
        domain: data.domain,
        adminDomain: data.adminDomain,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
      });
      toast.success('App registrado com sucesso! Faça login para continuar.');
      navigate('/login');
    } catch {
      toast.error('Erro ao registrar. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ backgroundColor: '#f8fafc' }}>
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4" style={{ backgroundColor: '#8b5cf6' }}>
            <FiShield className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: '#1e293b' }}>Registrar App</h1>
          <p className="text-sm mt-1" style={{ color: '#6b7280' }}>Configure seu app e crie o primeiro administrador</p>
        </div>

        <div className="rounded-xl border p-6" style={{ backgroundColor: '#ffffff', borderColor: 'rgba(0,0,0,0.06)' }}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <h3 className="text-sm font-semibold mb-3" style={{ color: '#1e293b' }}>Dados do App</h3>
              <div className="space-y-3">
                <FormField label="Nome do App" error={errors.appName?.message} required>
                  <Input placeholder="Meu SaaS" icon={FiGlobe} error={errors.appName?.message} {...register('appName')} />
                </FormField>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Domínio principal" error={errors.domain?.message} required description="Ex: app.exemplo.com">
                    <Input placeholder="app.exemplo.com" error={errors.domain?.message} {...register('domain')} />
                  </FormField>
                  <FormField label="Domínio admin" error={errors.adminDomain?.message} required description="Ex: admin.exemplo.com">
                    <Input placeholder="admin.exemplo.com" error={errors.adminDomain?.message} {...register('adminDomain')} />
                  </FormField>
                </div>
              </div>
            </div>

            <div className="border-t pt-5" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
              <h3 className="text-sm font-semibold mb-3" style={{ color: '#1e293b' }}>Administrador</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Nome" error={errors.firstName?.message} required>
                    <Input placeholder="João" icon={FiUser} error={errors.firstName?.message} {...register('firstName')} />
                  </FormField>
                  <FormField label="Sobrenome" error={errors.lastName?.message} required>
                    <Input placeholder="Silva" error={errors.lastName?.message} {...register('lastName')} />
                  </FormField>
                </div>
                <FormField label="Email" error={errors.email?.message} required>
                  <Input type="email" placeholder="admin@exemplo.com" icon={FiMail} error={errors.email?.message} {...register('email')} />
                </FormField>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Senha" error={errors.password?.message} required>
                    <Input type="password" placeholder="Mínimo 6 caracteres" icon={FiLock} error={errors.password?.message} {...register('password')} />
                  </FormField>
                  <FormField label="Confirmar senha" error={errors.confirmPassword?.message} required>
                    <Input type="password" placeholder="Repita a senha" icon={FiLock} error={errors.confirmPassword?.message} {...register('confirmPassword')} />
                  </FormField>
                </div>
              </div>
            </div>

            <Button type="submit" fullWidth loading={isLoading} size="lg">
              Registrar App
            </Button>
          </form>
        </div>

        <p className="text-center text-sm mt-6" style={{ color: '#6b7280' }}>
          Já tem um painel?{' '}
          <Link to="/login" className="font-medium" style={{ color: '#8b5cf6' }}>
            Fazer login
          </Link>
        </p>
      </div>
    </div>
  );
};
